import { ActionIcon, Checkbox, ColorInput, Group, Indicator, Loader, MultiSelect, Select, Switch, Text, Textarea, TextInput, TransferList, UnstyledButton, useMantineTheme } from '@mantine/core';
import {  DatePickerInput, DateTimePicker, TimeInput } from '@mantine/dates';
import { Dropzone, IMAGE_MIME_TYPE, MIME_TYPES, MS_EXCEL_MIME_TYPE, PDF_MIME_TYPE } from '@mantine/dropzone';
import { showNotification } from '@mantine/notifications';
import moment from 'moment';
import React from 'react';
import { FaCheckCircle, FaEye, FaFileImage, FaTimesCircle, FaUpload } from 'react-icons/fa';
import InputMask from "react-input-mask";
import NumberFormat from 'react-number-format';
import { UPLOAD_FILES } from '../services/files';

export enum InputFieldTypes{
    STRING = "string",
    NUMBER = "number",
    COLOR = "color",
    SELECT = "select",
    TRANSFER = "transfer",
    IMAGE = "image",
    FILE = "file",
    DATE = "date",
    DATETIME = "datetime",
    TIME = "time",
    DATERANGE = "daterange",
    BOOLEAN = "boolean",
    EXCEL = "excel",
    SWITCH = "switch",
    TEXTAREA = "textarea",
}
export interface InputFieldProps{
    name:string
    title?:string;
    value?: any;
    onChange?: any;
    loading?: boolean;
    options?: {value:string; label:string; group?: string}[];
    fieldType?: InputFieldTypes;
    multiple?: boolean;
    mask?: InputFieldMasks;
    customMask?: string | string[];
    maskPlaceholder?: string;
    maskChar?: string;
    icon?: any;
    style?: any;
    styles?: any;
    minRows?: number;
    maxRows?: number;
    placeholder?: string;
    clearable?: boolean;
    beforeMaskValueChange?: any;

    [x:string]: any;
}
export enum InputFieldMasks{
    MONEY = "money",
    CUSTOM = "custom",
}
export default function InputField(props : InputFieldProps){
    const {
        name,
        value,
        mask,
        customMask,
        maskPlaceholder = " ",
        maskChar = " ",
        title,
        fieldType = InputFieldTypes.STRING,
        loading,
        onChange,
        options = [],
        minRows = 5,
        maxRows = 8,
        multiple,
        icon,
        style,
        placeholder,
        clearable = true,
        styles,
        beforeMaskValueChange,
        ...others
    } = props; 
    const [files, setFiles] = React.useState([]);
    const theme = useMantineTheme();
    const [loadingUpload, setLoadingUpload] = React.useState(false);

    const onInsertFiles = (items) => {
        setLoadingUpload(true)
        UPLOAD_FILES(items)
        .then(res => {
            setLoadingUpload(false)
            setFiles(res.map(({url}) => url))
            onChange && onChange({[name]: multiple ? res.map(({url}) => url) : res[0] ? res[0].url : null}); 
        })
        .catch(err => {
            setLoadingUpload(false)
            showNotification({title: "Falha ao salvar arquivo", message: err.message, color: 'red'})
        })
    }
    
    if([InputFieldTypes.TRANSFER].includes(fieldType)){
        return <TransferList
            value={[
                (value || [])
                    .filter(r => (options || []).some(r2 => r2.value === r))
                    .map(r => ({value: r, label: options.find(r2 => r2.value === r)?.label})), 
                (options || []).filter(r => !(value || []).includes(r.value))
            ]}
            onChange={([items]) => onChange && onChange({[name]: items.map(item => item.value)})}
            titles={["Ativos", "Opções"]}
            searchPlaceholder="Procurar..."
            nothingFound="Não encontrado"
            breakpoint="sm"
            title={title}
        />
    }
    if([InputFieldTypes.SWITCH].includes(fieldType)){
        return <Switch
            label={title}
            checked={!!value}
            onChange={() => onChange({[name]: !value})}
            style={{...style, cursor: 'pointer', fontWeight: 'bold'}}
            {...others}
        />
    }
    if([InputFieldTypes.SELECT].includes(fieldType)){
        return multiple
        ? <MultiSelect    
            label={title}
            value={value ? value : []}
            placeholder={placeholder || "Selecione..."}
            searchable
            data={options.map(opt => ({label: `${opt.label}`, value: `${opt.value}`, group: opt.group}))}
            clearable={clearable}
            onChange={(v) => onChange({[name]: v})}
            style={style}
            styles={styles}
            {...others}
        />
        : <Select
            label={title}
            value={value ? `${value}` : null}
            placeholder={placeholder || "Selecione..."}
            searchable
            data={options.map(opt => ({label: `${opt.label}`, value: `${opt.value}`, group: opt.group}))}
            clearable={clearable}
            onChange={(v) => onChange({[name]: v})}
            style={style}
            styles={styles}
            {...others}
            autoFocus={false}
        />
    }
    if([InputFieldTypes.DATE].includes(fieldType)){
        return <div>
            <DatePickerInput
                label={title}
                value={value ? value : null}
                placeholder={placeholder || "Selecione..."}
                closeOnChange
                valueFormat="DD/MM/YYYY"
                // dateParser={(dateString) => moment(dateString, "DD/MM/YYYY").toDate()}
                clearable={clearable}
                // allowFreeInput
                onChange={(v) => onChange({[name]: v})}
                {...others}
            />
        </div>
    }
    if([InputFieldTypes.DATETIME].includes(fieldType)){
        return <div>
            <DateTimePicker
                label={title}
                value={value ? value : null}
                placeholder={placeholder || "Selecione..."}
                valueFormat="DD/MM/YYYY HH:mm"
                // dateParser={(dateString) => moment(dateString, "DD/MM/YYYY").toDate()}
                clearable={clearable}
                // allowFreeInput
                onChange={(v) => onChange({[name]: v})}
                {...others}
            />
        </div>
    }
    if([InputFieldTypes.TIME].includes(fieldType)){
        return <div>
            <TimeInput
                label={title}
                value={value ? value : null}
                placeholder={placeholder || "Selecione..."}
                onChange={(v) => onChange({[name]: v.target.value})}
                {...others}
            />
        </div>
    }
    if([InputFieldTypes.DATERANGE].includes(fieldType)){
        return <div style={{position: 'relative'}}>
            <DatePickerInput
                allowSingleDateInRange
                label={title}
                closeOnChange
                value={(value && value.length > 0) ? value : [null, null]}
                placeholder={placeholder || "Selecione um período"}
                clearable={clearable}
                valueFormat="DD/MM/YYYY"
                type="range"
                renderDay={(date) => (
                    <Indicator size={6} color="red" offset={8} disabled={moment().format("YYYY-MM-DD") !== moment(date).format("YYYY-MM-DD")}>
                        <div>{date.getDate()}</div>
                    </Indicator>
                )}
                onChange={(v: any) => {
                    // const qt = v.filter(v => v).length;
                    onChange({[name]: v})
                }}
                {...others}
            />
        </div>
    }
    if([InputFieldTypes.BOOLEAN].includes(fieldType)){
        return <Checkbox
            label={title}
            checked={value ? ["true", true].includes(value) : null}
            onChange={(v) => onChange({[name]: !!v.target.checked})}
        />
    }
    if([InputFieldTypes.IMAGE, InputFieldTypes.FILE, InputFieldTypes.EXCEL].includes(fieldType)){
        return <div style={{position: 'relative'}}>
            {title && <Text weight="bold" style={{fontSize: 14, marginBottom: 4}}>{title}</Text>}
            <Dropzone
                onDrop={(files) => {
                    console.log({files})
                    onInsertFiles(files)
                }}
                accept={
                    {
                        [InputFieldTypes.IMAGE]: IMAGE_MIME_TYPE,
                        [InputFieldTypes.EXCEL]: MS_EXCEL_MIME_TYPE,
                    }[fieldType] || [
                        MIME_TYPES.csv, MIME_TYPES.doc, MIME_TYPES.doc, MIME_TYPES.docx,
                        MIME_TYPES.exe, MIME_TYPES.gif, MIME_TYPES.jpeg, MIME_TYPES.mp4,
                        MIME_TYPES.pdf, MIME_TYPES.png, MIME_TYPES.ppt, MIME_TYPES.svg,
                        MIME_TYPES.xls, MIME_TYPES.xlsx, MIME_TYPES.zip, MIME_TYPES.webp,
                    ]
                }
                multiple={multiple}
                style={{ margin: 0, padding: 0, ...style }}
                maxSize={20000000}
                loading={loading}
                mt="lg"
            >
                <Group
                    position="center"
                    spacing="xl"
                    style={{ padding: 20, pointerEvents: "none" }}
                >
                    <Dropzone.Accept>
                        <FaUpload size={35} />
                        {((!!value && (!multiple || value.length > 0)) || files.length > 0) && (
                        <div style={{flexDirection: 'row', display: 'flex', alignItems: 'center'}}>
                            <FaCheckCircle size={35} />
                            <div style={{padding: '0 20px'}}>
                                <Text size="md" inline>
                                    Arquivo adicionado
                                </Text>
                                <Text size="sm" color="dimmed" inline mt={7}>
                                    {value?.name || title}
                                </Text>
                            </div>
                        </div>)}
                    </Dropzone.Accept>
                    <Dropzone.Reject>
                        <FaTimesCircle size={35} />
                    </Dropzone.Reject>
                    <Dropzone.Idle>
                        <div style={{flexDirection: 'row', display: 'flex', alignItems: 'center'}}>
                            <FaFileImage size={35} />
                            <div style={{flex: 1, padding: '0 20px'}}>
                                <Text size="md" inline>
                                    Arraste ou clique para selecionar o arquivo
                                </Text>
                                <Text size="xs" color="dimmed" inline mt={7}>
                                    O arquivo não deve exceder 50MB
                                </Text>
                            </div>
                        </div>
                    </Dropzone.Idle>
                </Group>
            </Dropzone>
            {!!loadingUpload 
            ? <ActionIcon
                size='sm'
                color="grape"
                variant="filled"
                style={{position: 'absolute', bottom: 5, right: 5}}
            ><Loader size="xs" color="white" /></ActionIcon>
            : !!value && (!multiple || value.length > 0) && <ActionIcon
                size='sm'
                color="grape"
                variant="filled"
                onClick={() => multiple ? value.forEach(v => window.open(v, "_blank")) : window.open(value, "_blank")}
                style={{position: 'absolute', bottom: 5, right: 5}}><FaEye /></ActionIcon>
            }
        </div>
    }
    if(fieldType === InputFieldTypes.COLOR){
        return <ColorInput
            label={title}
            value={value}
            title={title}
            placeholder={placeholder}
            icon={icon}
            style={style}
            styles={styles}
            onChange={(color) => onChange && onChange({[name]: color})}
            {...others}
        />
    }
    if(fieldType === InputFieldTypes.TEXTAREA){
        return <Textarea
            autosize
            minRows={minRows}
            maxRows={maxRows}
            label={title}
            value={value}
            title={title}
            placeholder={placeholder}
            icon={icon}
            style={style}
            styles={styles}
            onChange={(e) => onChange && onChange({[name]: e.target.value})}
            {...others}
        />
    }
    return <>
        <TextInput style={{display: 'none'}}/>
        {
            mask === InputFieldMasks.MONEY
            ? <div className="mantine-TextInput-root mantine-1oip40b">
                <label
                    htmlFor="mantine-gqpgnf2oj"
                    className="mantine-n9dkt1 mantine-TextInput-label"
                    id="mantine-gqpgnf2oj-label">{title}</label>
                <div className="mantine-TextInput-wrapper mantine-12sbrde">
                    <NumberFormat 
                        value={value ? parseFloat(value) : ""} 
                        displayType={'input'} 
                        thousandSeparator='.'
                        decimalSeparator=","
                        decimalScale={2}
                        fixedDecimalScale
                        prefix={'R$ '} 
                        onValueChange={(values) => {
                            const { formattedValue, value } = values;
                            onChange && onChange({ [name]: value });
                        }}
                        inputMode="decimal"
                        className='mantine-TextInput-filledVariant mantine-TextInput-input mantine-je1r5u'
                    />
                </div>
            </div>
            : mask === InputFieldMasks.CUSTOM
            ? <InputMask
                mask={customMask}
                value={props.value}
                maskPlaceholder={maskPlaceholder}
                maskChar={maskChar}
                onChange={(e) => onChange && onChange({[name]: e.target.value})}
                beforeMaskedValueChange={beforeMaskValueChange}
            >
                {(props) => 
                <TextInput
                    label={title}
                    value={value}
                    title={title}
                    placeholder={placeholder}
                    icon={icon}
                    style={style}
                    {...others}
                    {...props}
                />}
            </InputMask>
            : <div style={style}>
                <TextInput
                    label={title}
                    value={![undefined, null].includes(value) ? value : ""}
                    title={title}
                    placeholder={placeholder}
                    icon={icon}
                    styles={styles}
                    type={fieldType === InputFieldTypes.NUMBER ? "number" : undefined}
                    onChange={(e) => onChange && onChange({[name]: e.target.value})}
                    {...others}
                />
                {others.helper ? <Text size="xs" color="gray">{others.helper}</Text> : null}
            </div>
        }
    </>
} 